import { supabase } from '../services/supabase';
import { initDB as initCryptoDB } from './crypto';

// Define types for sync operations
interface SyncData {
  id?: string;
  [key: string]: unknown;
}

interface SyncItem {
  id?: number;
  table: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  data: SyncData;
  timestamp: string;
  synced: boolean;
}

// Initialize the database connection - use the same DB instance from crypto.ts
const getDB = async () => {
  // We use the same database initialization from crypto.ts to ensure consistency
  return initCryptoDB();
};

// Add item to sync queue
export const addToSyncQueue = async (
  table: string,
  operation: 'INSERT' | 'UPDATE' | 'DELETE',
  data: SyncData
): Promise<IDBValidKey | null> => {
  const db = await getDB();
  if (!db) return null;
  
  const timestamp = new Date().toISOString();
  
  const id = await db.add('sync_queue', {
    table,
    operation,
    data,
    timestamp,
    synced: false
  });
  
  return id;
};

// Check online status
export const isOnline = (): boolean => {
  return navigator.onLine;
};

// Clear invalid sync queue items (e.g., items with fields that don't exist in schema)
export const clearInvalidSyncItems = async (): Promise<void> => {
  const db = await getDB();
  if (!db) return;
  
  const tx = db.transaction('sync_queue', 'readwrite');
  const store = tx.objectStore('sync_queue');
  let cursor = await store.openCursor();
  
  while (cursor) {
    const item: SyncItem = cursor.value;
    
    // Check for invalid group_progress updates that try to set 'answer' field
    if (item.table === 'group_progress' && 
        item.operation === 'UPDATE' && 
        item.data.answer !== undefined) {
      console.log('Removing invalid sync item with answer field:', cursor.key);
      await cursor.delete();
    }
    
    cursor = await cursor.continue();
  }
  
  await tx.done;
  console.log('Cleared invalid sync items');
};


// Process sync queue
export const processSyncQueue = async (): Promise<boolean> => {
  if (!isOnline()) {
    return false;
  }
  
  const db = await getDB();
  if (!db) {
    console.error("Failed to get database for sync queue processing");
    return false;
  }

  // First, clear any invalid sync items
  await clearInvalidSyncItems();

  // First, collect all unsynced items
  const itemsToSync: Array<{ key: IDBValidKey; item: SyncItem }> = [];
  
  let tx = db.transaction('sync_queue', 'readonly');
  let store = tx.objectStore('sync_queue');
  let cursor = await store.openCursor();
  
  while (cursor) {
    const item: SyncItem = cursor.value;
    if (!item.synced) {
      itemsToSync.push({ key: cursor.key, item });
    }
    cursor = await cursor.continue();
  }
  
  await tx.done;
  
  // Now process each item
  let syncSuccess = true;
  
  for (const { key, item } of itemsToSync) {
    try {
      // Using type assertion for the specific table types
      const supabaseTable = supabase.from(item.table);
      
      switch (item.operation) {
        case 'INSERT': {
          // Don't add server_timestamp - the database sets it automatically
          const insertData = { ...item.data };
          
          // Use UPSERT for tables that have unique constraints
          // For user_answers and qr_scans, use the newest data on conflict
          if (item.table === 'user_answers') {
            // UPSERT: On conflict (user_id, qr_number), update with newest data
            const upsertResult = await (supabaseTable as any)
              .upsert(insertData as any, {
                onConflict: 'user_id,qr_number',
                ignoreDuplicates: false // Update with new data
              });
            if (upsertResult.error) throw upsertResult.error;
          } else if (item.table === 'group_progress') {
            // UPSERT: On conflict (group_id, qr_number), update with newest data
            const upsertResult = await (supabaseTable as any)
              .upsert(insertData as any, {
                onConflict: 'group_id,qr_number',
                ignoreDuplicates: false // Update with new data
              });
            if (upsertResult.error) throw upsertResult.error;
          } else if (item.table === 'qr_scans') {
            // For QR scans, check if a similar scan exists
            // We consider it a duplicate if same user_id and qr_number
            // Just insert and ignore duplicates (scans can happen multiple times)
            const insertResult = await (supabaseTable as any)
              .insert(insertData as any)
              .select();
            
            // Ignore duplicate errors for qr_scans
            if (insertResult.error && !insertResult.error.message.includes('duplicate key')) {
              throw insertResult.error;
            }
          } else {
            // For other tables, use regular insert
            const insertResult = await (supabaseTable as any).insert(insertData as any);
            if (insertResult.error) throw insertResult.error;
          }
          break;
        }
          
        case 'UPDATE': {
          // Don't add server_timestamp - the database sets it automatically
          const updateData = { ...item.data };
          
          // Handle different tables with different primary keys
          if (item.table === 'group_progress') {
            // group_progress uses composite key (group_id, qr_number)
            const { group_id, qr_number, ...dataToUpdate } = updateData;
            
            if (!group_id || !qr_number) {
              console.error('Missing group_id or qr_number for group_progress update');
              throw new Error('Missing required keys for group_progress update');
            }
            
            const updateResult = await (supabaseTable as any)
              .update(dataToUpdate as any)
              .eq('group_id', group_id)
              .eq('qr_number', qr_number);
            
            if (updateResult.error) throw updateResult.error;
          } else {
            // For tables with 'id' as primary key
            if (!item.data.id) {
              console.error(`Missing id for ${item.table} update`);
              throw new Error(`Missing id for ${item.table} update`);
            }
            
            const updateResult = await (supabaseTable as any)
              .update(updateData as any)
              .eq('id', item.data.id);
            
            if (updateResult.error) throw updateResult.error;
          }
          break;
        }
          
        case 'DELETE': {
          const deleteResult = await supabaseTable
            .delete()
            .eq('id', item.data.id || '');
          if (deleteResult.error) throw deleteResult.error;
          break;
        }
      }
      
      // Mark as synced in a new transaction
      const updateTx = db.transaction('sync_queue', 'readwrite');
      const updateStore = updateTx.objectStore('sync_queue');
      // Since keyPath is 'id', we need to include it in the object
      await updateStore.put({
        ...item,
        id: key,  // Include the key as the id field
        synced: true
      });
      await updateTx.done;
      
    } catch (error) {
      console.error(`Error syncing item ${key}:`, error);
      
      // Check if it's a duplicate key error - if so, mark as synced anyway
      // since the data is already in the database
      if (error instanceof Error && error.message.includes('duplicate key')) {
        console.log(`Duplicate entry for ${item.table}, marking as synced`);
        
        try {
          const updateTx = db.transaction('sync_queue', 'readwrite');
          const updateStore = updateTx.objectStore('sync_queue');
          await updateStore.put({
            ...item,
            id: key,
            synced: true
          });
          await updateTx.done;
        } catch (markError) {
          console.error('Error marking duplicate as synced:', markError);
          syncSuccess = false;
        }
      } else {
        syncSuccess = false;
      }
    }
  }
  
  // Clean up synced items that are older than 7 days
  const cleanupDate = new Date();
  cleanupDate.setDate(cleanupDate.getDate() - 7);
  
  // Null check for db before creating transaction
  if (!db) {
    return syncSuccess;
  }
  
  const cleanupTx = db.transaction('sync_queue', 'readwrite');
  const cleanupStore = cleanupTx.objectStore('sync_queue');
  let cleanupCursor = await cleanupStore.openCursor();
  
  while (cleanupCursor) {
    const item: SyncItem = cleanupCursor.value;
    
    if (item.synced && new Date(item.timestamp) < cleanupDate) {
      await cleanupCursor.delete();
    }
    
    cleanupCursor = await cleanupCursor.continue();
  }
  
  await cleanupTx.done;
  
  return syncSuccess;
};

// Get pending sync count
export const getPendingSyncCount = async (): Promise<number> => {
  const db = await getDB();
  if (!db) return 0;
  
  const tx = db.transaction('sync_queue', 'readonly');
  const store = tx.objectStore('sync_queue');
  
  const items = await store.getAll();
  const pendingItems = items.filter(item => !item.synced);
  
  return pendingItems.length;
};

// Function to check if there are unsynced changes
export const checkUnsyncedChanges = async (): Promise<boolean> => {
  const count = await getPendingSyncCount();
  return count > 0;
};

// Initialize the sync service
export const initSyncService = async () => {
  console.log('Initializing sync service...');
  
  // Clear any invalid sync items on initialization
  await clearInvalidSyncItems();
  
  // Set up event listeners for online status changes
  const handleOnlineStatus = () => {
    if (navigator.onLine) {
      console.log('App is online. Processing sync queue...');
      processSyncQueue().catch(console.error);
    } else {
      console.log('App is offline. Sync paused.');
    }
  };
  
  // Add event listeners
  window.addEventListener('online', handleOnlineStatus);
  window.addEventListener('offline', handleOnlineStatus);
  
  // Set up periodic sync check (every 5 minutes)
  setInterval(() => {
    if (navigator.onLine) {
      console.log('Periodic sync check...');
      processSyncQueue().catch(console.error);
    }
  }, 5 * 60 * 1000);
  
  // Initial sync attempt
  if (navigator.onLine) {
    processSyncQueue().catch(console.error);
  }
  
  console.log('Sync service initialized');
};